/**
 * Investment Calculation Utilities - ENHANCED VERSION
 * Properly handles API response data from the backend investment calculator
 * Primary method: Uses historical daily returns with compound growth calculation
 * Fallback method: Uses default return rates if API data is unavailable
 */

/**
 * Calculate Compound Annual Growth Rate (CAGR)
 */
export const calculateCAGR = (beginningValue, endingValue, numberOfYears) => {
    if (beginningValue <= 0 || endingValue <= 0 || numberOfYears <= 0) {
        return 0;
    }
    return (Math.pow(endingValue / beginningValue, 1 / numberOfYears) - 1) * 100;
};

/**
 * Calculate compound interest - FALLBACK METHOD ONLY
 */
export const calculateCompoundInterest = (principal, rate, time, compoundingFrequency = 12) => {
    if (principal <= 0 || rate < 0 || time <= 0) {
        return principal;
    }
    return principal * Math.pow(1 + rate / compoundingFrequency, compoundingFrequency * time);
};

/**
 * Calculate total return percentage
 */
export const calculateTotalReturn = (initialValue, finalValue) => {
    if (initialValue <= 0) {
        return 0;
    }
    return ((finalValue - initialValue) / initialValue) * 100;
};

/**
 * Process API response data - PRIMARY METHOD
 * Handles the structured response from the backend API
 */
export const processAPIResponse = (apiData) => {
    if (!apiData) {
        console.error('No API data provided');
        return null;
    }
    
    // Check for error status in API response
    if (apiData.status && apiData.status !== 200) {
        console.error('API returned error:', apiData.message);
        return null;
    }
    
    // Handle different response formats
    let summary, portfolioValue, dailyReturns, cumulativeReturns;
    
    if (apiData.summary) {
        // Direct format
        summary = apiData.summary;
        portfolioValue = apiData.portfolioValue || [];
        dailyReturns = apiData.dailyreturn || [];
        cumulativeReturns = apiData.cumulative || [];
    } else if (apiData.data && apiData.data.summary) {
        // Nested format
        summary = apiData.data.summary;
        portfolioValue = apiData.data.portfolioValue || [];
        dailyReturns = apiData.data.dailyreturn || [];
        cumulativeReturns = apiData.data.cumulative || [];
    } else {
        console.error('Invalid API response format:', apiData);
        return null;
    }
    
    if (!summary) {
        console.error('No summary data found in API response');
        return null;
    }
    
    return {
        initialInvestment: summary.initial_investment || 0,
        finalValue: summary.final_value || 0,
        totalReturn: summary.total_return_pct || 0,
        cagr: summary.cagr_pct || 0,
        volatility: summary.volatility_pct || 0,
        sharpeRatio: summary.sharpe_ratio || 0,
        investmentPeriod: summary.investment_period_years || 0,
        numberOfStocks: summary.number_of_stocks || 0,
        cumulativeMultiplier: summary.cumulative_multiplier || 1,
        portfolioData: portfolioValue,
        dailyReturns: dailyReturns,
        cumulativeReturns: cumulativeReturns,
        totalRecordsProcessed: summary.total_records_processed || portfolioValue.length,
        calculationMethod: 'historical_compound_returns'
    };
};

/**
 * Calculate portfolio value from cumulative multiplier - PREFERRED METHOD
 * This is the most accurate method using the API's compound calculation
 */
export const calculateValueFromMultiplier = (initialInvestment, cumulativeMultiplier) => {
    if (!cumulativeMultiplier || cumulativeMultiplier <= 0) {
        console.warn('Invalid cumulative multiplier, using original investment amount');
        return initialInvestment;
    }
    
    return initialInvestment * cumulativeMultiplier;
};

/**
 * Validate API response
 */
export const validateAPIResponse = (apiData) => {
    if (!apiData) {
        return { isValid: false, error: 'No API response data' };
    }
    
    if (apiData.status && apiData.status !== 200) {
        return { isValid: false, error: apiData.message || 'API returned error status' };
    }
    
    const processed = processAPIResponse(apiData);
    if (!processed) {
        return { isValid: false, error: 'Failed to process API response' };
    }
    
    return { isValid: true, data: processed };
};

/**
 * Extract latest value from mixed API response
 */
export const extractLatestValue = (source, keys = ['value']) => {
    if (source == null) return 0;

    const items = Array.isArray(source) ? source : [source];
    const last = items[items.length - 1];

    if (typeof last === 'number') return last;
    if (typeof last === 'object') {
        for (const key of keys) {
            const val = last[key];
            if (typeof val === 'number') return val;
        }
    }

    return 0;
};

/**
 * Calculate portfolio value using API return data with compound formula
 * FIXED: Better handling of API data and more robust calculations
 */
export const calculatePortfolioValueFromAPI = (initialInvestment, apiPortfolioData, years) => {


    if (!Array.isArray(apiPortfolioData) || apiPortfolioData.length === 0 || initialInvestment <= 0 || years <= 0) {
        return {
            finalValue: initialInvestment,
            totalReturn: 0,
            cagr: 0,
            yearlyData: []
        };
    }

    const yearlyData = [];
    let currentPortfolioValue = initialInvestment;

    // Filter out invalid data points
    const validDataPoints = apiPortfolioData.filter(dataPoint =>
        dataPoint &&
        (dataPoint.result !== undefined || dataPoint.value !== undefined)
    );


    if (validDataPoints.length === 0) {
        return {
            finalValue: initialInvestment,
            totalReturn: 0,
            cagr: 0,
            yearlyData: []
        };
    }

    validDataPoints.forEach((dataPoint, index) => {
        // Extract return value - try multiple possible keys
        let returnValue = dataPoint.result || dataPoint.value || dataPoint.return || 0;

        // Convert string to number if needed
        if (typeof returnValue === 'string') {
            returnValue = parseFloat(returnValue) || 0;
        }


        // Handle different return formats
        // If return value is greater than 1, assume it's already a multiplier
        // If return value is between 0 and 1, assume it's a decimal return rate
        // If return value is greater than 1 but less than 10, assume it's a percentage that needs conversion
        let factor;
        if (returnValue > 10) {
            // Likely a percentage like 15.5 (meaning 15.5%)
            factor = 1 + (returnValue / 100);
        } else if (returnValue > 1) {
            // Likely already a multiplier like 1.155
            factor = returnValue;
        } else if (returnValue >= 0 && returnValue <= 1) {
            // Decimal return like 0.155
            factor = 1 + returnValue;
        } else {
            // Negative or invalid value
            factor = 1;
        }


        currentPortfolioValue *= factor;

        yearlyData.push({
            year: index + 1,
            value: currentPortfolioValue,
            totalReturn: calculateTotalReturn(initialInvestment, currentPortfolioValue),
            returnFactor: factor,
            apiResult: returnValue,
            originalApiData: dataPoint
        });

    });

    const finalValue = yearlyData.length > 0 ? yearlyData[yearlyData.length - 1].value : initialInvestment;
    const totalReturn = calculateTotalReturn(initialInvestment, finalValue);
    const cagr = calculateCAGR(initialInvestment, finalValue, years);


    return {
        finalValue,
        totalReturn,
        cagr,
        yearlyData
    };
};

/**
 * Fallback calculation using default returns
 * ENHANCED: Better error handling and logging
 */
export const calculatePortfolioValue = (initialInvestment, selectedStocks, years) => {
    if (!Array.isArray(selectedStocks) || selectedStocks.length === 0 || initialInvestment <= 0 || years <= 0) {
        return {
            finalValue: initialInvestment,
            totalReturn: 0,
            cagr: 0,
            yearlyData: []
        };
    }

    const stockReturns = selectedStocks.map(stock => {
        const returnRate = DEFAULT_STOCK_RETURNS[stock] || 0.10;
        return returnRate;
    });

    const averageReturn = stockReturns.reduce((sum, r) => sum + r, 0) / stockReturns.length;

    let currentPortfolioValue = initialInvestment;
    const yearlyData = [];

    for (let year = 1; year <= years; year++) {
        const factor = 1 + averageReturn;
        currentPortfolioValue *= factor;

        yearlyData.push({
            year,
            value: currentPortfolioValue,
            totalReturn: calculateTotalReturn(initialInvestment, currentPortfolioValue),
            returnFactor: factor,
            annualReturn: averageReturn
        });
    }

    const finalValue = currentPortfolioValue;
    const totalReturn = calculateTotalReturn(initialInvestment, finalValue);
    const cagr = calculateCAGR(initialInvestment, finalValue, years);

    return {
        finalValue,
        totalReturn,
        cagr,
        yearlyData
    };
};

/**
 * Calculate portfolio value with historical stock data
 */
export const calculatePortfolioWithHistoricalData = (initialInvestment, historicalData, years) => {
    if (!Array.isArray(historicalData) || historicalData.length === 0) {
        return {
            finalValue: initialInvestment,
            totalReturn: 0,
            cagr: 0,
            monthlyData: []
        };
    }

    const endDate = new Date();
    const startDate = new Date();
    startDate.setFullYear(endDate.getFullYear() - years);

    const filteredData = historicalData.filter(data => {
        const dataDate = new Date(data.date);
        return dataDate >= startDate && dataDate <= endDate;
    });

    if (filteredData.length === 0) {
        return {
            finalValue: initialInvestment,
            totalReturn: 0,
            cagr: 0,
            monthlyData: []
        };
    }

    filteredData.sort((a, b) => new Date(a.date) - new Date(b.date));

    const firstDay = filteredData[0];
    const stockSymbols = firstDay.stocks?.map(stock => stock.symbol) || [];
    const investmentPerStock = initialInvestment / stockSymbols.length;

    const initialPrices = {};
    stockSymbols.forEach(symbol => {
        const stock = firstDay.stocks.find(s => s.symbol === symbol);
        if (stock) {
            initialPrices[symbol] = stock.price;
        }
    });

    const monthlyData = [];

    filteredData.forEach((dataPoint) => {
        if (Array.isArray(dataPoint.stocks)) {
            let portfolioValue = 0;

            dataPoint.stocks.forEach(stock => {
                const initialPrice = initialPrices[stock.symbol];
                if (!initialPrice || initialPrice === 0) return;

                const currentPrice = stock.price;
                const priceMultiplier = currentPrice / initialPrice;

                portfolioValue += investmentPerStock * priceMultiplier;
            });

            monthlyData.push({
                date: dataPoint.date,
                value: portfolioValue,
                totalReturn: calculateTotalReturn(initialInvestment, portfolioValue)
            });
        }
    });

    const finalValue = monthlyData.length > 0 ? monthlyData[monthlyData.length - 1].value : initialInvestment;
    const totalReturn = calculateTotalReturn(initialInvestment, finalValue);
    const cagr = calculateCAGR(initialInvestment, finalValue, years);

    return {
        finalValue,
        totalReturn,
        cagr,
        monthlyData
    };
};

/**
 * ENHANCED: Better number formatting with proper validation
 */
export const formatCurrency = (value, currency = 'BDT', decimals = 2) => {
    // Handle null, undefined, or NaN values
    if (value == null || isNaN(value)) {
        return `${currency} 0.00`;
    }

    // Convert to number if it's a string
    const numValue = typeof value === 'string' ? parseFloat(value) : value;

    if (isNaN(numValue)) {
        return `${currency} 0.00`;
    }

    return `${currency} ${numValue.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    })}`;
};

/**
 * ENHANCED: Better percentage formatting with proper validation
 */
export const formatPercentage = (value, decimals = 2) => {
    // Handle null, undefined, or NaN values
    if (value == null || isNaN(value)) {
        return '0.00%';
    }

    // Convert to number if it's a string
    const numValue = typeof value === 'string' ? parseFloat(value) : value;

    if (isNaN(numValue)) {
        return '0.00%';
    }

    return `${numValue.toFixed(decimals)}%`;
};

/**
 * ENHANCED: Add debugging helper function
 */
export const debugApiResponse = (apiData) => {

    if (apiData.portfolioValue) {
    }

    if (apiData.totalreturn) {
    }

    if (apiData.csgr) {
    }

};

/**
 * Default stock return rates
 * ENHANCED: Added more realistic return rates and better documentation
 */
export const DEFAULT_STOCK_RETURNS = {
    'BRACBANK': 0.12,      // 12% annual return
    'CITYBANK': 0.10,      // 10% annual return
    'SQURPHARMA': 0.15,    // 15% annual return
    'RENATA': 0.14,        // 14% annual return
    'BXPHARMA': 0.13,      // 13% annual return
    'MARICO': 0.11,        // 11% annual return
    'RECKITTBEN': 0.09,    // 9% annual return
    'OLYMPIC': 0.08,       // 8% annual return
    'BATBC': 0.10,         // 10% annual return
    'LHBL': 0.12,          // 12% annual return
    'UNILEVERCL': 0.11,    // 11% annual return
    'HEIDELBCEM': 0.07,    // 7% annual return
    'LINDEBD': 0.09,       // 9% annual return
    'BERGERPBL': 0.13,     // 13% annual return
    'GP': 0.14,            // 14% annual return
    'UPGDCL': 0.16         // 16% annual return
};